import type { ReasonerResponseType } from '@/interfaces/reasoner.interface';
import type { Node } from '@neo4j-nvl/base';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useProjectContext } from '@/context/ProjectContext';

interface TableGraphContextType {
  nodeToOpen: Node | null;
  setNodeToOpen: (node: Node | null) => void;
  reasonerResponse: ReasonerResponseType;
  setReasonerResponse: (response: ReasonerResponseType) => void;
  setAvailableIds: (ids: Set<string>) => void;
  availableIds: Set<string>;
  graphNodes: Node[];
  setGraphNodes: (nodes: Node[]) => void;
}

const TableGraphContext = createContext<TableGraphContextType | undefined>(
  undefined
);

interface TableGraphProviderProps {
  children: ReactNode;
}

export const TableGraphProvider: React.FC<TableGraphProviderProps> = ({
  children,
}) => {
  const [nodeToOpen, setNodeToOpen] = useState<Node | null>(null);
  const [reasonerResponse, setReasonerResponse] =
    useState<ReasonerResponseType>(null);
  const [availableIds, setAvailableIds] = useState<Set<string>>(new Set());
  const [graphNodes, setGraphNodes] = useState<Node[]>([]);

  // This context lives above the router, so a node/graph handoff (e.g. "open
  // this node in reMap") survives navigating away from Data Explorer. When the
  // user switches projects, that handoff belongs to the old project and must be
  // discarded — otherwise returning to reMap derives a stale `singleNode` view
  // instead of the new project's schema. Reset on project change (not on the
  // initial value) so reMap always opens on schema for a freshly selected project.
  const selectedProjectId = useProjectContext((s) => s.selectedProjectId);
  const prevProjectIdRef = useRef(selectedProjectId);
  useEffect(() => {
    if (prevProjectIdRef.current === selectedProjectId) return;
    prevProjectIdRef.current = selectedProjectId;
    setNodeToOpen(null);
    setReasonerResponse(null);
    setAvailableIds(new Set());
    setGraphNodes([]);
  }, [selectedProjectId]);

  const value: TableGraphContextType = {
    nodeToOpen,
    setNodeToOpen,
    reasonerResponse,
    setReasonerResponse,
    availableIds,
    setAvailableIds,
    graphNodes,
    setGraphNodes,
  };

  return (
    <TableGraphContext.Provider value={value}>
      {children}
    </TableGraphContext.Provider>
  );
};

export const useTableGraphContext = (): TableGraphContextType => {
  const context = useContext(TableGraphContext);
  if (context === undefined) {
    throw new Error(
      'useTableGraphContext must be used within a TableGraphProvider'
    );
  }
  return context;
};
